import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

export default function CarModel({ src, label, scale = 90, cameraZ = 180 }) {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;

    const mountEl = mountRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 5000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mountEl.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 1.2);
    key.position.set(40, 60, 80);
    scene.add(key);

    let mesh;
    let root;

    const applyMaterial = (object) => {
      object.traverse((child) => {
        if (child.isMesh) {
          if (!child.material || Array.isArray(child.material)) {
            child.material = new THREE.MeshStandardMaterial({
              color: 0x9b1b2f,
              metalness: 0.6,
              roughness: 0.35,
              emissive: new THREE.Color('#3a0a10'),
              emissiveIntensity: 0.4,
            });
          }
          mesh = child;
        }
      });
    };

    const scaleObject = (object) => {
      let target = null;
      object.traverse((child) => {
        if (child.isMesh && child.geometry) {
          target = child.geometry;
        }
      });
      if (!target) return;
      target.computeBoundingBox();
      const box = target.boundingBox;
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      const targetScale = scale / maxDim;
      object.scale.setScalar(targetScale);
    };

    const loadOBJ = () => {
      const loader = new OBJLoader();
      loader.load(src, (object) => {
        root = object;
        applyMaterial(object);
        scaleObject(object);
        scene.add(object);
        resize();
      });
    };

    const loadGLTF = () => {
      const loader = new GLTFLoader();
      loader.load(src, (gltf) => {
        root = gltf.scene;
        applyMaterial(root);
        scaleObject(root);
        scene.add(root);
        resize();
      });
    };

    const loadSTL = () => {
      const loader = new STLLoader();
      loader.load(src, (geometry) => {
        geometry.center();
        geometry.computeBoundingBox();
        const box = geometry.boundingBox;
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const targetScale = scale / maxDim;
        const material = new THREE.MeshStandardMaterial({
          color: 0x9b1b2f,
          metalness: 0.6,
          roughness: 0.35,
          emissive: new THREE.Color('#3a0a10'),
          emissiveIntensity: 0.4,
        });
        mesh = new THREE.Mesh(geometry, material);
        root = mesh;
        mesh.scale.setScalar(targetScale);
        mesh.rotation.x = -Math.PI / 2;
        scene.add(mesh);
        resize();
      });
    };

    const lower = src.toLowerCase();
    if (lower.endsWith('.stl')) {
      loadSTL();
    } else if (lower.endsWith('.glb') || lower.endsWith('.gltf')) {
      loadGLTF();
    } else {
      loadOBJ();
    }

    const resize = () => {
      const width = mountEl.clientWidth;
      const height = mountEl.clientHeight;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.position.set(0, 0, cameraZ);
      camera.updateProjectionMatrix();
    };

    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    let raf;
    const animate = () => {
      if (root) {
        root.rotation.y += 0.004;
        root.rotation.x += 0.001;
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    resize();

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
      renderer.dispose();
      mountEl.innerHTML = '';
    };
  }, [src, scale, cameraZ]);

  return (
    <div className="car-model">
      <div className="car-model-canvas" ref={mountRef} aria-label={label}></div>
      <div className="car-model-label">{label}</div>
    </div>
  );
}
